// 117 — Certificado Médico, SNS-MSP/HCU-form.117/2021. NO es el certificado
// de reposo del IESS (CUS/PRAS) — el HCU pide horas de reposo, no días, y
// el pie va con cédula del profesional, nunca un código ACESS (ver
// `etiquetaIdentificacion` en `useDatosEncabezadoMsp`/`PanelFirmaImpresion`).
// Reemplaza por completo el formulario genérico anterior (título/contenido/
// días de reposo en texto libre, que no correspondía al HCU real).
import { useMedplum } from '@medplum/react-hooks';
import type { CompositionSection } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { FormularioDiscretoProps, Par } from './index';
import { CampoDictado } from './CampoDictado';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { conCodigoCie } from './camposSimplesDatos';
import { codeDeDiagnostico, SelectorDiagnosticoCie10, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { faltantesReposo117, fechaTexto117, horasEnteras117, horasNumerosYLetras, textoHospitalizacion117, textoReposo117 } from './bloques117Datos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';

const DIAGNOSTICO_VACIO: DiagnosticoCie10Valor = { texto: '', cie10: null };
const NUM_DIAGNOSTICOS = 4;

function narrativa(texto: string): CompositionSection['text'] {
  const escapado = texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return { status: 'generated', div: `<div xmlns="http://www.w3.org/1999/xhtml"><p>${escapado}</p></div>` };
}

export function Formulario117({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [servicio, setServicio] = useState('');
  // Especialidad de ESTA atención: precargada con la del practicante, editable. `null` = el usuario no la tocó (se sigue la del practicante).
  const [especialidadEditada, setEspecialidadEditada] = useState<string | null>(null);
  const [fechaAtencion, setFechaAtencion] = useState('');
  const [horaDesde, setHoraDesde] = useState('');
  const [horaHasta, setHoraHasta] = useState('');
  const [hospitalizacion, setHospitalizacion] = useState(false);
  const [fechaIngreso, setFechaIngreso] = useState('');
  const [fechaAlta, setFechaAlta] = useState('');
  const [reposo, setReposo] = useState(false);
  const [horasReposo, setHorasReposo] = useState('');
  const [reposoDesde, setReposoDesde] = useState('');
  const [reposoHasta, setReposoHasta] = useState('');
  const [diagnosticos, setDiagnosticos] = useState<DiagnosticoCie10Valor[]>(Array.from({ length: NUM_DIAGNOSTICOS }, () => DIAGNOSTICO_VACIO));
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  const datosBaseEncabezado = useDatosEncabezadoMsp(subject, campos, '117', t('formularios.f117.titulo'), { etiquetaIdentificacion: t('formularios.f117.cedula'), unaHoja: true, servicio: servicio.trim() || (hospitalizacion ? 'Hospitalización' : undefined) });
  const especialidad = especialidadEditada ?? datosBaseEncabezado.especialidad;
  // Solo el 117 sobreescribe la especialidad del encabezado compartido (el papel la pide como dato de la atención).
  const datosEncabezadoMsp = { ...datosBaseEncabezado, especialidad: especialidad.trim() };

  const diagnosticosConTexto = diagnosticos.filter((d) => d.texto.trim());

  const handleSubmit = (): Promise<void> => guardar(async () => {
    const reposoDatos = { reposo, horas: horasReposo, desde: reposoDesde, hasta: reposoHasta };
    const faltan = faltantesReposo117(reposoDatos);
    if (faltan.length > 0) throw new Error(faltan.join(' '));
    const profile = medplum.getProfile();
    const secciones: CompositionSection[] = [];
    if (servicio.trim()) secciones.push({ title: t('formularios.f117.servicio'), text: narrativa(servicio.trim()) });
    if (especialidad.trim()) secciones.push({ title: t('formularios.f117.especialidad'), text: narrativa(especialidad.trim()) });
    if (fechaAtencion) secciones.push({ title: t('formularios.f117.fechaAtencion'), text: narrativa(fechaTexto117(fechaAtencion)) });
    if (horaDesde || horaHasta) secciones.push({ title: t('formularios.f117.horaAtencion'), text: narrativa(`${horaDesde || '—'} a ${horaHasta || '—'}`) });
    if (hospitalizacion) secciones.push({ title: t('formularios.f117.hospitalizacion'), text: narrativa(textoHospitalizacion117(fechaIngreso, fechaAlta)) });
    // El papel no permite dejar Reposo en blanco: se registra tanto el "Sí" (con horas y fechas) como el "No".
    secciones.push({ title: t('formularios.f117.reposo'), text: narrativa(textoReposo117(reposoDatos)) });
    diagnosticosConTexto.forEach((d, i) => {
      secciones.push({
        title: `${t('formularios.f117.diagnostico')} ${i + 1}`,
        text: narrativa(conCodigoCie(d)),
        code: codeDeDiagnostico(d),
      });
    });

    await medplum.createResource({
      resourceType: 'Composition',
      status: 'final',
      type: { text: 'Certificado Médico' },
      subject,
      encounter,
      date: new Date().toISOString(),
      author: profile ? [{ reference: `${profile.resourceType}/${profile.id}` }] : [],
      title: t('formularios.f117.tituloPorDefecto'),
      section: secciones,
    });

    const pares: Par[] = [
      // El servicio ya sale en el encabezado ("Servicio: ..."): no se repite como par.
      ...(fechaAtencion ? [{ pregunta: t('formularios.f117.fechaAtencion'), respuesta: fechaTexto117(fechaAtencion) }] : []),
      ...(horaDesde || horaHasta ? [{ pregunta: t('formularios.f117.horaAtencion'), respuesta: `${horaDesde || '—'} a ${horaHasta || '—'}` }] : []),
      { pregunta: t('formularios.f117.hospitalizacion'), respuesta: hospitalizacion ? t('formulario.si') : t('formulario.no') },
      ...(hospitalizacion && fechaIngreso ? [{ pregunta: t('formularios.f117.fechaIngreso'), respuesta: fechaTexto117(fechaIngreso) }] : []),
      ...(hospitalizacion && fechaAlta ? [{ pregunta: t('formularios.f117.fechaAlta'), respuesta: fechaTexto117(fechaAlta) }] : []),
      { pregunta: t('formularios.f117.reposo'), respuesta: reposo ? t('formulario.si') : t('formulario.no') },
      ...(reposo && horasEnteras117(horasReposo) !== undefined ? [{ pregunta: t('formularios.f117.horasReposo'), respuesta: horasNumerosYLetras(horasEnteras117(horasReposo)!) }] : []),
      ...(reposo && reposoDesde ? [{ pregunta: t('formularios.f117.reposoDesde'), respuesta: fechaTexto117(reposoDesde) }] : []),
      ...(reposo && reposoHasta ? [{ pregunta: t('formularios.f117.reposoHasta'), respuesta: fechaTexto117(reposoHasta) }] : []),
      ...diagnosticosConTexto.map((d, i) => ({ pregunta: `${t('formularios.f117.diagnostico')} ${i + 1}`, respuesta: conCodigoCie(d) })),
    ];
    onGuardado(pares, datosEncabezadoMsp);
  });

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f117.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <CampoDictado id="f117-servicio" etiqueta={t('formularios.f117.servicio')} valor={servicio} onChange={setServicio} />

      <CampoDictado id="f117-especialidad" etiqueta={t('formularios.f117.especialidad')} valor={especialidad} onChange={setEspecialidadEditada} />

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-6 sm:col-span-4">
          <Label htmlFor="f117-fecha-atencion">{t('formularios.f117.fechaAtencion')}</Label>
          <Input id="f117-fecha-atencion" type="date" value={fechaAtencion} onChange={(e) => setFechaAtencion(e.target.value)} />
        </div>
        <div className="col-span-6 sm:col-span-4">
          <Label htmlFor="f117-hora-desde">{t('formularios.f117.horaDesde')}</Label>
          <Input id="f117-hora-desde" type="time" value={horaDesde} onChange={(e) => setHoraDesde(e.target.value)} />
        </div>
        <div className="col-span-6 sm:col-span-4">
          <Label htmlFor="f117-hora-hasta">{t('formularios.f117.horaHasta')}</Label>
          <Input id="f117-hora-hasta" type="time" value={horaHasta} onChange={(e) => setHoraHasta(e.target.value)} />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-hce-ink">
        <Checkbox checked={hospitalizacion} onCheckedChange={(v) => setHospitalizacion(v === true)} />
        {t('formularios.f117.hospitalizacion')}
      </label>
      {hospitalizacion && (
        <div className="grid grid-cols-12 gap-3 rounded-md border border-border p-3">
          <div className="col-span-6">
            <Label htmlFor="f117-fecha-ingreso">{t('formularios.f117.fechaIngreso')}</Label>
            <Input id="f117-fecha-ingreso" type="date" value={fechaIngreso} onChange={(e) => setFechaIngreso(e.target.value)} />
          </div>
          <div className="col-span-6">
            <Label htmlFor="f117-fecha-alta">{t('formularios.f117.fechaAlta')}</Label>
            <Input id="f117-fecha-alta" type="date" value={fechaAlta} onChange={(e) => setFechaAlta(e.target.value)} />
          </div>
        </div>
      )}

      <label className="flex items-center gap-2 text-sm text-hce-ink">
        <Checkbox checked={reposo} onCheckedChange={(v) => setReposo(v === true)} />
        {t('formularios.f117.reposo')}
      </label>
      {reposo && (
        <div className="grid grid-cols-12 gap-3 rounded-md border border-border p-3">
          <div className="col-span-12 sm:col-span-4">
            <Label htmlFor="f117-horas-reposo">{t('formularios.f117.horasReposo')}</Label>
            <Input
              id="f117-horas-reposo"
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={horasReposo}
              onChange={(e) => setHorasReposo(e.target.value)}
            />
          </div>
          <div className="col-span-6 sm:col-span-4">
            <Label htmlFor="f117-reposo-desde">{t('formularios.f117.reposoDesde')}</Label>
            <Input id="f117-reposo-desde" type="date" value={reposoDesde} onChange={(e) => setReposoDesde(e.target.value)} />
          </div>
          <div className="col-span-6 sm:col-span-4">
            <Label htmlFor="f117-reposo-hasta">{t('formularios.f117.reposoHasta')}</Label>
            <Input id="f117-reposo-hasta" type="date" value={reposoHasta} onChange={(e) => setReposoHasta(e.target.value)} />
          </div>
        </div>
      )}

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f117.diagnosticos')}</h4>
      {diagnosticos.map((d, i) => (
        <SelectorDiagnosticoCie10
          key={i}
          label={`${t('formularios.f117.diagnostico')} ${i + 1}`}
          value={d}
          onChange={(v) => setDiagnosticos((prev) => prev.map((prev_d, idx) => (idx === i ? v : prev_d)))}
        />
      ))}

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
